import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Chip,
  Stack,
  CircularProgress,
  Tooltip,
  Paper
} from '@mui/material';
import {
  AutoAwesome,
  Close,
  Lightbulb,
  TipsAndUpdates,
  Psychology,
  CheckCircle
} from '@mui/icons-material';
import aiService from '../../services/aiService';
import DialogCloseButton from '../common/DialogCloseButton';

const AIQuoteDialog = ({ open, onClose, selectedRequest, quoteForm, setQuoteForm, onSubmit }) => {
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(true);
  const [generatingQuote, setGeneratingQuote] = useState(false);

  // Generate AI suggestions when dialog opens
  useEffect(() => {
    if (open && selectedRequest && !aiSuggestions) {
      generateAISuggestions();
    }
  }, [open, selectedRequest]);

  const generateAISuggestions = async () => {
    setLoadingAI(true);
    try {
      const requestDetails = selectedRequest.request_details || {};
      const context = {
        title: requestDetails.title || 'Verification Request',
        description: requestDetails.description || '',
        type: selectedRequest.verification_type || 'general',
        location: selectedRequest.location?.address || '',
        budget: selectedRequest.budget || 0
      };

      // Generate suggestions for the quote
      const suggestions = await aiService.generateProposalSuggestions(context);
      setAiSuggestions(suggestions);
    } catch (error) {
      console.error('Error generating AI suggestions:', error);
    } finally {
      setLoadingAI(false);
    }
  };

  const handleGenerateQuote = async () => {
    setGeneratingQuote(true);
    try {
      const requestDetails = selectedRequest.request_details || {};
      const context = {
        title: requestDetails.title || 'Verification Request',
        description: requestDetails.description || '',
        type: selectedRequest.verification_type || 'general',
        location: selectedRequest.location?.address || '',
        verifierExperience: 'General Verifier' // Could come from profile
      };

      const generatedText = await aiService.generateProposalText(context);
      setQuoteForm({ ...quoteForm, message: generatedText });
    } catch (error) {
      console.error('Error generating quote:', error);
      alert('Failed to generate quote text. Please try again.');
    } finally {
      setGeneratingQuote(false);
    }
  };

  const handleSmartPricing = () => {
    if (aiSuggestions?.suggestedPrice) {
      setQuoteForm({ ...quoteForm, proposedFee: aiSuggestions.suggestedPrice });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome color="primary" />
          <Typography variant="h6">Respond to Verification Job</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {selectedRequest && (
          <Box>
            {/* Job Header */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
              <Typography variant="h6" gutterBottom>
                {selectedRequest.request_details?.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedRequest.request_details?.description}
              </Typography>
            </Paper>

            {/* AI Suggestions Panel */}
            <Collapse in={showAISuggestions && !loadingAI && aiSuggestions}>
              <Card sx={{ mb: 3, bgcolor: 'info.50', borderLeft: 4, borderColor: 'info.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Psychology color="info" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        AI Suggestions
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setShowAISuggestions(false)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>

                  {aiSuggestions && (
                    <Stack spacing={2}>
                      {/* Suggested Quote and Time hidden per user request
                      {aiSuggestions.suggestedPrice && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Suggested Fee
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={`$${aiSuggestions.suggestedPrice}`} 
                              color="primary" 
                              size="small"
                              onClick={handleSmartPricing}
                              sx={{ cursor: 'pointer' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Based on similar verifications
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {aiSuggestions.suggestedHours && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Estimated Time
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={`${aiSuggestions.suggestedHours} hours`} 
                              color="primary" 
                              size="small"
                              onClick={handleSmartTiming}
                              sx={{ cursor: 'pointer' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Typical for this verification type
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      */}

                      {aiSuggestions.keyPoints && aiSuggestions.keyPoints.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Key Points to Mention
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {aiSuggestions.keyPoints.map((point, idx) => (
                              <Chip key={idx} label={point} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      <Alert severity="info" icon={<Lightbulb />}>
                        <Typography variant="caption">
                          Click on suggested values to auto-fill fields, or use AI generation buttons below
                        </Typography>
                      </Alert>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Collapse>

            {loadingAI && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            )}

            {/* Form Fields */}
            <Grid container spacing={2}>
              {/* Your Fee */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Your Fee ($)"
                  type="number"
                  value={quoteForm.proposedFee}
                  onChange={(e) => setQuoteForm({ ...quoteForm, proposedFee: e.target.value })}
                  InputProps={{
                    endAdornment: aiSuggestions?.suggestedPrice && (
                      <Tooltip title="Use AI suggestion">
                        <IconButton size="small" onClick={handleSmartPricing}>
                          <AutoAwesome fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    )
                  }}
                />
              </Grid>

              {/* Available Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Available Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={quoteForm.availableDate}
                  onChange={(e) => setQuoteForm({ ...quoteForm, availableDate: e.target.value })}
                  helperText={(() => {
                    try {
                      const scheduling = typeof selectedRequest.scheduling === 'string'
                        ? JSON.parse(selectedRequest.scheduling)
                        : selectedRequest.scheduling;
                      return scheduling?.requestedDate || scheduling?.scheduledDateTime
                        ? "Pre-populated from client's request. You can adjust if needed."
                        : "Proposed date for verification";
                    } catch {
                      return "Proposed date for verification";
                    }
                  })()}
                />
              </Grid>

              {/* Message with AI */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2">Message *</Typography>
                  <Button
                    size="small"
                    startIcon={generatingQuote ? <CircularProgress size={16} /> : <AutoAwesome />}
                    onClick={handleGenerateQuote}
                    disabled={generatingQuote}
                  >
                    {generatingQuote ? 'Improving...' : 'Improve with AI'}
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  placeholder="Explain why you're qualified, when you can inspect, and how you'll approach the verification..."
                  value={quoteForm.message}
                  onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  <TipsAndUpdates fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Highlight your experience, certifications, and why you're the best fit
                </Typography>
              </Grid>
            </Grid>

            {/* AI Tips */}
            <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Tips for a Strong Response
              </Typography>
              <Typography variant="caption" component="div">
                • Be specific about your qualifications and experience<br />
                • Mention relevant certifications or training<br />
                • Explain your quality assurance process<br />
                • Highlight any specialized equipment you'll use<br />
                • Be competitive but fair with your pricing
              </Typography>
            </Alert>

            {/* Validation Error Message */}
            {(!quoteForm.proposedFee || !quoteForm.message) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Missing Required Fields
                </Typography>
                <Typography variant="caption" component="div">
                  {!quoteForm.proposedFee && '• Your Fee is required\n'}
                  {!quoteForm.message && '• Message is required (use "Improve with AI" button to generate)\n'}
                </Typography>
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Tooltip title={
          !quoteForm.proposedFee ? "Please enter your fee" :
          !quoteForm.message ? "Please enter a message" :
          "Send your response"
        }>
          <span>
            <Button
              onClick={onSubmit}
              variant="contained"
              color="primary"
              startIcon={<AutoAwesome />}
              disabled={!quoteForm.proposedFee || !quoteForm.message}
            >
              Send Response
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default AIQuoteDialog;
